import { useQuery } from '@tanstack/react-query';
import { MigrationImporterSchema, type MigrationImporterDto } from '@taqon/contracts';
import { z } from 'zod';
import { getData } from '../api/client';

/** The declared importers of the migration module (SDD §21.2). */
export const IMPORTERS = '/api/v1/migration/importers';

/** The declared importers (target record types, their CSV columns and sample rows). */
export function useImporters(enabled = true) {
  return useQuery({
    queryKey: ['migration', 'importers'],
    queryFn: () => getData(IMPORTERS, z.array(MigrationImporterSchema)),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** One CSV cell: quoted when it holds a comma, a quote or a line break. */
function cell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function csvHref(rows: readonly (readonly string[])[]): string {
  const text = rows.map((row) => row.map(cell).join(',')).join('\r\n');
  return `data:text/csv;charset=utf-8,${encodeURIComponent(`${text}\r\n`)}`;
}

/** An importer's CSV template: the header row, for the operator to fill (a data: URL). */
export function templateHref(importer: MigrationImporterDto): string {
  return csvHref([importer.columns.map((c) => c.name)]);
}

/**
 * An importer's sample CSV (CLR-59): the header row and the importer's synthetic example rows, a
 * column a row leaves out being an empty cell. Undefined when the importer declares no sample.
 */
export function sampleHref(importer: MigrationImporterDto): string | undefined {
  if (importer.sample.length === 0) return undefined;
  const names = importer.columns.map((c) => c.name);
  return csvHref([names, ...importer.sample.map((row) => names.map((name) => row[name] ?? ''))]);
}
